import { type TransformerConfig } from '../common/types';
import {
  createAbsentAsTransformer,
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import { UserNoteAssignee, UserNoteCreator, UserNoteType } from './enums';
import { DBUserNoteSchema, UserNoteSchema } from './schemas';

const USER_NOTE_CREATORS = new Set<string>(Object.values(UserNoteCreator));

export const UserNoteCodecConfig: TransformerConfig<
  typeof UserNoteSchema,
  typeof DBUserNoteSchema,
  'id'
> = {
  name: 'UserNote',
  clientSchema: UserNoteSchema,
  dbSchema: DBUserNoteSchema,
  transformers: [
    // ownerId + type ↔ composite userId key (type#userId)
    createTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>({
      encoder: {
        sourceFields: ['ownerId', 'type'] as const,
        dependencies: ['ownerId', 'type'] as const,
        encode: ({ ownerId, type }) => ({
          userId: `${type}#${ownerId}`,
        }),
      },
      decoder: {
        sourceFields: ['userId'] as const,
        decode: ({ userId: compositeUserId }) => {
          // istanbul ignore next -- defaults unreachable due to schema validation requiring exactly 2 parts
          const [type = '', ownerId = ''] = compositeUserId.split('#');

          return {
            type: type as UserNoteType,
            ownerId,
          };
        },
      },
    }),

    // The indexes are keyed on the assignee and the dates, and the stored row requires the name
    createRenameTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>(
      'assigneeId',
      'assignee',
      { absentAs: UserNoteAssignee.Unassigned, alwaysStored: true }
    ),
    createAbsentAsTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>('assigneeName', '', {
      alwaysStored: true,
    }),
    createAbsentAsTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>('dueDatetime', 0, {
      alwaysStored: true,
    }),
    createAbsentAsTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>(
      'priorityDatetime',
      0,
      { alwaysStored: true }
    ),

    // createdBy (a user) or createdBySystem ↔ creator, which holds either
    createTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>({
      encoder: {
        sourceFields: ['createdBy', 'createdBySystem'] as const,
        encode: ({ createdBy, createdBySystem }) => {
          const creator = createdBySystem ?? createdBy;
          return creator === undefined ? {} : { creator };
        },
      },
      decoder: {
        sourceFields: ['creator'] as const,
        decode: ({ creator }) => {
          if (creator === undefined) {
            return {};
          }
          return USER_NOTE_CREATORS.has(creator)
            ? { createdBySystem: creator as UserNoteCreator }
            : { createdBy: creator };
        },
      },
    }),

    // active: boolean ↔ active: 'true' | 'false'
    createTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>({
      encoder: {
        sourceFields: ['active'] as const,
        encode: ({ active }) => ({
          active: active ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['active'] as const,
        decode: ({ active }) => ({
          active: active === 'true',
        }),
      },
    }),

    // activeBillingRelatedKey: computed index key
    // Consumes billingRelated (passes through) and active, computes activeBillingRelatedKey
    createTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>({
      encoder: {
        sourceFields: ['active', 'billingRelated'] as const,
        dependencies: ['active', 'billingRelated'] as const,
        encode: ({ active, billingRelated }) => ({
          activeBillingRelatedKey: `${active}#${billingRelated}`,
          billingRelated,
        }),
      },
      // Decoder consumes activeBillingRelatedKey to prevent it from being passed through
      decoder: {
        sourceFields: ['activeBillingRelatedKey'] as const,
        decode: () => ({}),
      },
    }),
  ],
  derived: { id: note => buildRecordId('userNote', note) },
};

export const encodeUserNoteToDb = createEncodeToDbFn(UserNoteCodecConfig);
export const decodeUserNoteFromDb = createDecodeFromDbFn(UserNoteCodecConfig);
